/**
 * Backend Status Panel - Displays real-time health of all backends
 */

import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, AlertCircle, Circle, Server, Database, Activity, Zap } from "lucide-react";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";

export function BackendStatusPanel() {
  const { health, systemMode } = useDashboardStore();

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <HealthCard
          title="Local Kernel"
          status={health?.kernel}
          icon={Activity}
          description="Centralized AGI backend"
          active={systemMode === "centralized"}
        />
        <HealthCard
          title="Convex Cloud"
          status={health?.convex}
          icon={Database}
          description="Distributed backend"
          active={systemMode === "decentralized"}
        />
        <HealthCard
          title="MCP Servers"
          statusGroup={health?.mcpServers}
          icon={Server}
          description="Model Context Protocol"
          count={health?.mcpServers?.filter((s: any) => s.connected).length || 0}
        />
        <HealthCard
          title="LLM Providers"
          statusGroup={health?.llmProviders}
          icon={Zap}
          description="AI model integrations"
          count={health?.llmProviders?.filter((p: any) => p.healthy).length || 0}
        />
      </div>
    </div>
  );
}

interface HealthCardProps {
  title: string;
  status?: any;
  statusGroup?: any[];
  icon: any;
  description: string;
  active?: boolean;
  count?: number;
}

function HealthCard({ title, status, statusGroup, icon: Icon, description, active, count }: HealthCardProps) {
  const getStatusInfo = () => {
    if (statusGroup) {
      const groupHealthy = statusGroup.filter((s: any) => s.healthy).length;
      const groupTotal = statusGroup.length;
      if (groupHealthy === groupTotal && groupTotal > 0)
        return { color: "bg-green-500", icon: CheckCircle, text: "All Healthy" };
      if (groupHealthy > 0) return { color: "bg-yellow-500", icon: AlertCircle, text: "Partial" };
      if (groupTotal > 0) return { color: "bg-red-500", icon: XCircle, text: "None Connected" };
      return { color: "bg-gray-500", icon: Circle, text: "Not Configured" };
    }

    if (!status) return { color: "bg-gray-500", icon: Circle, text: "Unknown" };
    if (status.healthy && status.connected)
      return { color: "bg-green-500", icon: CheckCircle, text: "Online", latency: status.latency };
    if (status.connected) return { color: "bg-yellow-500", icon: AlertCircle, text: "Degraded" };
    return { color: "bg-red-500", icon: XCircle, text: "Offline", error: status.error };
  };

  const { color, icon: StatusIcon, text, latency, error } = getStatusInfo();

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={cn(
        "relative rounded-xl border p-4 transition-all",
        active
          ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.1)]"
          : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"
      )}
    >
      {/* Active Indicator */}
      {active && (
        <div className="absolute top-2 right-2">
          <div className="flex h-2 w-2 rounded-full bg-[hsl(var(--primary))] animate-pulse" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={cn("flex h-10 w-10 items-center justify-center rounded-lg", active ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))]")}
          >
            <Icon className={cn("h-5 w-5", active ? "text-black" : "text-[hsl(var(--muted-foreground))]")} />
          </div>
          <div>
            <div className="font-semibold">{title}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{description}</div>
          </div>
        </div>

        {/* Status Badge */}
        <div className={cn("flex items-center gap-1.5 rounded-full px-2 py-1", `${color}/20`)}>
          <StatusIcon className={cn("h-3.5 w-3.5", color.replace("bg-", "text-"))} />
          <span className={cn("text-xs font-medium", color.replace("bg-", "text-"))}>{count !== undefined ? `${count} Ready` : text}</span>
        </div>
      </div>

      {/* Details */}
      <AnimatePresence>
        {(latency || error || status?.details) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2 overflow-hidden"
          >
            {latency && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">Latency</span>
                <span className="font-mono">{latency}ms</span>
              </div>
            )}
            {status?.details && (
              <div className="flex items-start gap-2 text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">Status</span>
                <span className="flex-1">{status.details}</span>
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 text-xs text-[hsl(var(--destructive))]">
                <span>Error</span>
                <span className="flex-1 line-clamp-2">{error}</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/**
 * Compact Status Indicator (for header/sidebar)
 */
export function BackendStatusIndicator() {
  const { health } = useDashboardStore();

  const kernelStatus = health?.kernel?.connected ? health?.kernel?.healthy : false;
  const convexStatus = health?.convex?.connected ? health?.convex?.healthy : false;

  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="flex items-center gap-1.5">
        <div
          className={cn("h-2 w-2 rounded-full", kernelStatus ? "bg-green-500 animate-pulse" : "bg-gray-500")}
        />
        <span className="text-[hsl(var(--muted-foreground))]">Kernel</span>
      </div>
      <div className="flex items-center gap-1.5">
        <div
          className={cn("h-2 w-2 rounded-full", convexStatus ? "bg-green-500 animate-pulse" : "bg-gray-500")}
        />
        <span className="text-[hsl(var(--muted-foreground))]">Convex</span>
      </div>
    </div>
  );
}
